import React from "react";
import {
  Title,
  Caption,
  TableWrapper,
  StyledTable,
  TableHead,
  TableHeader,
  TableRow,
  TableEntry,
  MobileHeader,
  MobileEntry,
} from "./table.styled";
import { TableSliceFragment } from "../../types/graphql";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import { htmlSerializer } from "../../utils/htmlSerializer";
import { Slice } from "@prismicio/types";
import { IPageContext } from "../../types/types";
import { SharedSliceType } from "../shared-slice/shared-slice";

type TableEntryNumber =
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "10";
type TableEntryKey = `column_${TableEntryNumber}_entry`;
type TableEntryHeaderKey = `column_${TableEntryNumber}_header`;

export type ITableProps = Slice<
  SharedSliceType,
  TableSliceFragment["primary"],
  TableSliceFragment["items"][number]
> & { type: SharedSliceType };

const Table = ({
  slice,
  context,
}: SliceComponentProps<ITableProps, IPageContext>) => {
  const tableData = slice.primary;
  const tableEntries = slice.items;
  // Get the number of columns in table by finding the
  // the last non-null header.
  const getNumColumns = () => {
    if (tableData) {
      let i = 10;
      while (
        tableData[`column_${i}_header` as TableEntryHeaderKey] == null &&
        i > 0
      ) {
        i--;
      }
      return i;
    }
    return 0;
  };

  const getTableHeaders = () => {
    if (!tableData) return [];
    const results: Array<string> = [];
    Object.keys(tableData)?.map((key, index) => {
      if (
        key.match(/column_[\d]+_header/g) &&
        Object.values(tableData)[index] !== null
      ) {
        results.push(Object.values(tableData)[index]?.toString() || "");
      }
    });
    return results;
  };

  const numColumns = getNumColumns();
  const tableHeaders = getTableHeaders();
  return (
    <section className="slice-section">
      <div className="container">
        {tableData.heading && <h2>{tableData.heading}</h2>}
        {tableData?.table_title && <Title>{tableData?.table_title}</Title>}
        <TableWrapper>
          <StyledTable>
            <TableHead>
              <TableRow>
                {tableData &&
                  // Only output table headers for defined headers
                  tableHeaders.map((header) => {
                    return <TableHeader key={header}>{header}</TableHeader>;
                  })}
              </TableRow>
            </TableHead>
            <tbody>
              {/* Render row with entries */}
              {tableEntries?.map((row, rowIndex) => {
                if (!row) {
                  return;
                }

                // Create array of entries
                const entries = [];
                for (
                  let entryIndex = 0;
                  entryIndex < numColumns;
                  entryIndex++
                ) {
                  entries.push(
                    <TableEntry key={entryIndex}>
                      {row[
                        `column_${entryIndex + 1}_entry` as TableEntryKey
                      ] && (
                        <>
                          <MobileHeader>
                            {tableHeaders[entryIndex]}
                          </MobileHeader>
                          <MobileEntry>
                            <PrismicRichText
                              field={
                                row[
                                  `column_${
                                    entryIndex + 1
                                  }_entry` as TableEntryKey
                                ]?.richText
                              }
                              components={htmlSerializer}
                            />
                          </MobileEntry>
                        </>
                      )}
                    </TableEntry>
                  );
                }
                return <TableRow key={rowIndex}>{entries}</TableRow>;
              })}
            </tbody>
          </StyledTable>
        </TableWrapper>
        {tableData?.table_caption_rich_text && (
          <Caption>
            <PrismicRichText
              field={tableData?.table_caption_rich_text.richText}
              components={htmlSerializer}
            />
          </Caption>
        )}
      </div>
    </section>
  );
};

export default Table;
